import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, ValidationErrors, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';


@Component({
  selector: 'rms-multi-select',
  standalone: true,
  templateUrl: './multi-select-field.component.html',
  styleUrls: ['../rms-styles.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MultiSelectFieldComponent),
      multi: true
    }
  ],
  imports: [CommonModule, FormsModule]
})
export class MultiSelectFieldComponent implements ControlValueAccessor, Validator {
  @Input() options: { value: any; label: string }[] = [];
  @Input() label: string = 'Select Options';
  @Input() disabled: boolean = false;
  @Input() placeholder: string = 'Choose options';
  @Input() maxHeight: string = '200px'; // ✅ Customizable height for scrollable list
  @Input() requiredOptions: number = 0; // ✅ Number of options that must be selected
  @Input() exclusiveValues: any[] = [];
  selectedValues: any[] = [];

  onChange = (_: any) => { };
  onTouched = () => { };
  private onValidatorChange = () => { };


  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }


  validate(control: AbstractControl): ValidationErrors | null {
    if (this.requiredOptions > 0 && this.selectedValues.length < this.requiredOptions) {
      return { requiredOptions: true };
    }
    return null; 
  }

  writeValue(value: any[]): void {
    this.selectedValues = value ?? [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  isSelected(value: any): boolean {
    return this.selectedValues.includes(value);
  }


  toggleSelection(value: any): void {
    if (this.isSelected(value)) {
      this.selectedValues = this.selectedValues.filter(v => v !== value);
    } else {
      this.selectedValues = [...this.selectedValues, value]; // ✅ immutable update
    }

    this.onChange(this.selectedValues);
    this.onTouched();

    // ✅ force validator + parent form update
    this.onValidatorChange?.();
  }


  trackByValue(_: number, option: { value: any }) {
    return option.value;
  }

  private isExclusive(value: any): boolean {
    return this.exclusiveValues.includes(value);
  }

  private hasExclusiveSelected(): boolean {
    return this.selectedValues.some(v => this.isExclusive(v));
  }

  private hasNonExclusiveSelected(): boolean {
    return this.selectedValues.some(v => !this.isExclusive(v));
  }
  isDisabledOption(value: any): boolean {
    if (this.disabled) return true;

    const isExclusive = this.isExclusive(value);
    const hasExclusive = this.hasExclusiveSelected();
    const hasOthers = this.hasNonExclusiveSelected();

    // If THIS option is exclusive:
    if (isExclusive) {
      // Disable if any non-exclusive option is selected
      return hasOthers && !this.isSelected(value);
    }

    // If THIS option is normal:
    // Disable if an exclusive one is selected
    return hasExclusive && !this.isSelected(value);
  }
}
